/**
 * The one HTTP seam this app's own requests go through (ORDR-41).
 *
 * Three things every request here needs, and that are easy to get wrong one
 * call site at a time:
 *
 * - THE BASE URL comes from `VITE_API_BASE_URL` (see `config/env.ts`), so the
 *   deployed bundle can point at the gateway's origin.
 * - `credentials: "include"`. PAIO authenticates by the first-party `paio_sid`
 *   cookie, and in production the gateway is cross-origin; without this a
 *   request rides anonymous and only appears to work in dev.
 * - A FRESH `X-Correlation-ID` per request, returned with the result, so an
 *   error state can show the id a support ticket needs.
 *
 * A RESULT, NEVER A THROW. A non-200 and a network failure both come back as
 * `ok: false`, with `status: null` for the network case. The caller decides
 * what each means; nothing here guesses, and nothing here turns a failure into
 * an empty body.
 */

import { API_BASE } from "../config/env.js";

export type ApiResult =
  | { readonly ok: true; readonly status: number; readonly body: unknown; readonly correlationId: string }
  | { readonly ok: false; readonly status: number | null; readonly correlationId: string };

/**
 * Every status, for callers whose contract distinguishes them (ORDR-42): a
 * 304 is "caught up", a 410 is "revoked", a 409 is "re-quote". `body` is the
 * parsed JSON when there was any, else `null`. `status: null` is a network
 * failure.
 */
export interface ApiResponse {
  readonly status: number | null;
  readonly body: unknown;
  readonly correlationId: string;
}

export interface ApiRequest {
  readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** Serialised as JSON when present. */
  readonly json?: unknown;
  /**
   * Sent as multipart when present (ORDR-190, a menu upload). No Content-Type
   * is set: the browser writes it WITH the boundary, and a hand-set one has none.
   */
  readonly form?: FormData;
  /** Extra headers, such as `Authorization: Cart <secret>`. Never logged. */
  readonly headers?: Readonly<Record<string, string>>;
}

export interface ApiClient {
  getJson(path: string): Promise<ApiResult>;
  request(path: string, init?: ApiRequest): Promise<ApiResponse>;
}

export interface ApiClientOptions {
  /** Defaults to `VITE_API_BASE_URL`, else same-origin `/api/v1`. */
  readonly baseUrl?: string;
  /** Injected for tests. Defaults to the global `fetch` at call time. */
  readonly fetchImpl?: typeof fetch;
  /** Injected for tests. Defaults to `crypto.randomUUID`. */
  readonly correlationId?: () => string;
}

function randomCorrelationId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  // RFC 4122 v4 shape from Math.random, only where no Web Crypto exists. A
  // correlation id is a trace handle, not a secret.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const nibble = Math.floor(Math.random() * 16);
    return (char === "x" ? nibble : (nibble & 0x3) | 0x8).toString(16);
  });
}

function join(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = options.baseUrl ?? API_BASE;
  const nextCorrelationId = options.correlationId ?? randomCorrelationId;

  return {
    async request(path: string, init: ApiRequest = {}): Promise<ApiResponse> {
      const correlationId = nextCorrelationId();
      const fetchImpl = options.fetchImpl ?? globalThis.fetch;
      const headers: Record<string, string> = {
        Accept: "application/json",
        "X-Correlation-ID": correlationId,
        ...init.headers,
      };
      if (init.json !== undefined) {
        headers["Content-Type"] = "application/json";
      }

      let response: Response;
      try {
        response = await fetchImpl(join(baseUrl, path), {
          method: init.method ?? "GET",
          credentials: "include",
          headers,
          ...(init.form !== undefined
            ? { body: init.form }
            : init.json === undefined
              ? {}
              : { body: JSON.stringify(init.json) }),
        });
      } catch {
        return { status: null, body: null, correlationId };
      }

      let body: unknown = null;
      if (response.status !== 204 && response.status !== 304) {
        try {
          body = await response.json();
        } catch {
          body = null;
        }
      }
      return { status: response.status, body, correlationId };
    },

    async getJson(path: string): Promise<ApiResult> {
      const correlationId = nextCorrelationId();
      // Read at call time, not captured at construction, so a test that stubs
      // the global after the client exists still intercepts the request.
      const fetchImpl = options.fetchImpl ?? globalThis.fetch;

      let response: Response;
      try {
        response = await fetchImpl(join(baseUrl, path), {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json", "X-Correlation-ID": correlationId },
        });
      } catch {
        return { ok: false, status: null, correlationId };
      }

      if (response.status !== 200) {
        return { ok: false, status: response.status, correlationId };
      }

      try {
        return { ok: true, status: response.status, body: await response.json(), correlationId };
      } catch {
        // A 200 whose body is not JSON is not an answer.
        return { ok: false, status: response.status, correlationId };
      }
    },
  };
}
